'use client';

import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: string;
  popular: boolean;
}

const PACKAGES: CreditPackage[] = [
  { id: 'starter', name: 'Starter', credits: 500, price: '4,99', popular: false },
  { id: 'creator', name: 'Creator', credits: 1500, price: '11,99', popular: true },
  { id: 'pro', name: 'Pro', credits: 5000, price: '34,99', popular: false },
  { id: 'ultimate', name: 'Ultimate', credits: 15000, price: '89,99', popular: false },
];

interface PaymentFormProps {
  clientSecret: string;
  selectedPackage: CreditPackage;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ clientSecret, selectedPackage, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMsg(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/dashboard?payment=success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message || 'Pagamento falhou. Tenta novamente.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
    }
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Pacote {selectedPackage.name}</p>
          <p className="text-xs text-muted-foreground">{selectedPackage.credits.toLocaleString('pt-PT')} créditos</p>
        </div>
        <p className="text-xl font-bold text-foreground">€{selectedPackage.price}</p>
      </div>

      <PaymentElement />

      {errorMsg && (
        <div className="p-3 rounded-lg bg-danger/10 border border-danger/20 text-xs text-danger flex items-center gap-2">
          <Icon name="ExclamationTriangleIcon" size={14} />
          {errorMsg}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-border transition-all duration-150 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              A processar...
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={16} />
              Pagar €{selectedPackage.price}
            </>
          )}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
        <Icon name="LockClosedIcon" size={11} />
        Pagamento seguro via Stripe
      </p>
    </form>
  );
}

interface CreditsPurchaseModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function CreditsPurchaseModal({ onClose, onSuccess }: CreditsPurchaseModalProps) {
  const { user, refreshCredits } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const stripePromise = getStripe();

  const handleSelectPackage = async (pkg: CreditPackage) => {
    if (!user) return;
    setSelectedPackage(pkg);
    setIsLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      const { data, error: fnError } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
      }>('create-payment-intent', {
        body: {
          packageId: pkg.id,
          userId: user.id,
          userEmail: user.email,
        },
      });

      if (fnError || !data?.clientSecret) {
        throw new Error((data as { error?: string })?.error ?? fnError?.message ?? 'Erro ao iniciar pagamento');
      }

      setClientSecret(data.clientSecret);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao iniciar pagamento';
      setError(message);
      setSelectedPackage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentSuccess(true);
    // Wait a moment for webhook to process, then refresh credits
    setTimeout(async () => {
      await refreshCredits();
      onSuccess?.();
    }, 2000);
  };

  const handleBack = () => {
    setSelectedPackage(null);
    setClientSecret(null);
    setError(null);
  };

  if (paymentSuccess) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md bg-card border border-success/30 rounded-2xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-5">
            <Icon name="CheckCircleIcon" size={36} className="text-success" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Pagamento confirmado!</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Os teus créditos serão adicionados em instantes.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 transition-all duration-150"
          >
            Continuar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {selectedPackage && (
              <button onClick={handleBack} className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors mr-1">
                <Icon name="ArrowLeftIcon" size={16} className="text-muted-foreground" />
              </button>
            )}
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Icon name="BoltIcon" size={18} className="text-primary" variant="solid" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Comprar créditos</h2>
              <p className="text-xs text-muted-foreground">Créditos nunca expiram</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted/50 transition-colors">
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-danger/10 border border-danger/20 text-xs text-danger flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={14} />
              {error}
            </div>
          )}

          {!selectedPackage ? (
            /* Package selection */
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">Escolhe o pacote que melhor se adapta às tuas necessidades:</p>
              {PACKAGES.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handleSelectPackage(pkg)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-150 hover:border-primary/50 hover:bg-primary/5 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed text-left ${
                    pkg.popular ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${pkg.popular ? 'bg-primary/20' : 'bg-muted'}`}>
                      <Icon name="BoltIcon" size={16} className={pkg.popular ? 'text-primary' : 'text-muted-foreground'} variant="solid" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{pkg.name}</span>
                        {pkg.popular && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full gradient-primary text-white">Popular</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{pkg.credits.toLocaleString('pt-PT')} créditos</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-bold text-foreground">€{pkg.price}</p>
                    {isLoading && selectedPackage?.id === pkg.id && (
                      <Icon name="ArrowPathIcon" size={14} className="animate-spin text-primary ml-auto" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : clientSecret ? (
            /* Payment form */
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#7c3aed',
                    colorBackground: '#1a1a2e',
                    colorText: '#e2e8f0',
                    borderRadius: '12px',
                  },
                },
              }}
            >
              <PaymentForm
                clientSecret={clientSecret}
                selectedPackage={selectedPackage}
                onSuccess={handlePaymentSuccess}
                onCancel={handleBack}
              />
            </Elements>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Icon name="ArrowPathIcon" size={24} className="animate-spin text-primary" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
